/**
 * UK timezone-aware date formatters.
 *
 * Backend stores everything in UTC (ISO 8601). UI must render in Europe/London,
 * which is BST (UTC+1) in summer and GMT (UTC+0) in winter. Intl.DateTimeFormat
 * handles DST automatically — never hardcode an offset.
 *
 * Use these helpers everywhere instead of string-slicing ISO timestamps.
 */

const LONDON = "Europe/London";

const _fmt = (opts) =>
  new Intl.DateTimeFormat("en-GB", { timeZone: LONDON, ...opts });

const _toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value) ? null : value;
  if (typeof value !== "string") return null;
  // Treat naive ISO ("2026-05-30T16:45:00") as UTC by appending Z, so JS doesn't
  // parse it in the browser's local timezone.
  const needsZ = value.includes("T") && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(value);
  const d = new Date(needsZ ? `${value}Z` : value);
  return isNaN(d) ? null : d;
};

/** "BST" in summer, "GMT" in winter. */
export const ukTzLabel = (value) => {
  const d = _toDate(value) || new Date();
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: LONDON,
    timeZoneName: "short",
  }).formatToParts(d);
  return parts.find((p) => p.type === "timeZoneName")?.value || "GMT";
};

/** "29 May 2026" — no time. */
export const fmtDate = (value) => {
  const d = _toDate(value);
  if (!d) return "";
  return _fmt({ day: "2-digit", month: "short", year: "numeric" }).format(d);
};

/** "2026-05-29" — Europe/London date, suitable for <input type="date"> values. */
export const fmtISODate = (value) => {
  const d = _toDate(value);
  if (!d) return "";
  const parts = _fmt({
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value || "";
  return `${get("year")}-${get("month")}-${get("day")}`;
};

/** "16:45" — 24h time in Europe/London. */
export const fmtTime = (value) => {
  const d = _toDate(value);
  if (!d) return "";
  return _fmt({ hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
};

/** "29 May 2026, 16:45 BST" — full, with TZ label. */
export const fmtDateTime = (value) => {
  const d = _toDate(value);
  if (!d) return "";
  return `${fmtDate(d)}, ${fmtTime(d)} ${ukTzLabel(d)}`;
};

/** "29 May 16:45" — compact, no year, no TZ label. */
export const fmtDateTimeShort = (value) => {
  const d = _toDate(value);
  if (!d) return "";
  const date = _fmt({ day: "2-digit", month: "short" }).format(d);
  return `${date} ${fmtTime(d)}`;
};

/** "2026-05-29 16:45" — fixed-width, sortable; preferred for tables. */
export const fmtISODateTime = (value) => {
  const d = _toDate(value);
  if (!d) return "";
  return `${fmtISODate(d)} ${fmtTime(d)}`;
};

/** "2026-05-29T16:45" — value format for <input type="datetime-local"> in London. */
export const fmtDateTimeLocal = (value) => {
  const d = _toDate(value);
  if (!d) return "";
  return `${fmtISODate(d)}T${fmtTime(d)}`;
};
